import { GetStaticProps } from 'next';
import { ParsedUrlQuery } from 'querystring';

/* Components */
import CategoryPageContainer from '@scenes/ServicesCategoryPage';
import { getLayout } from '@components/PageLayouts/Layout/Standard';

/* Data */
import headerData from '@integrations/preval/headerdata.preval';

/* Apis */
import { skus } from '@api/apis';

/* Utils */
import { fetchCategoryPageData } from '@utils/categoryPageData';

/* Constants */
import { PAGE_TYPE_CATEGORY } from '@constants/pages';
import { SEVEN_DAYS } from '@constants/CacheRevalidate';

/* Types */
import { Children } from 'types/base.types';
import { TServicesCategoryProps } from 'types/serviceCategory.types';

/*
 * Our base Services Sku Page. The page proper is broken out just in case we ever need to
 * house that container in another layout.
 * */
function ServicesCategoryPage(props: TServicesCategoryProps): Children {
  return <CategoryPageContainer {...props} />;
}

ServicesCategoryPage.pageAttributes = { page: PAGE_TYPE_CATEGORY };
ServicesCategoryPage.Layout = getLayout({ promo: true, footer: true });

export default ServicesCategoryPage;

/**
 * ---------------------------------------------------------------------------------------
 * Static Generation
 * @param params
 * ---------------------------------------------------------------------------------------
 */
interface StaticParams extends ParsedUrlQuery {
  seoName: string;
}

/**
 * - Get sku data
 * - Get reviews for sku
 * - Get routes for navigation (predone for rank/column)
 *
 * @param {GetStaticPropsContext<ParsedUrlQuery, PreviewData>} context
 * @returns {Promise<*>}
 */
export const getStaticProps: GetStaticProps = async context => {
  const { categoryName } = context.params as StaticParams;

  if (!categoryName || Array.isArray(categoryName)) {
    return {
      notFound: true,
    };
  }

  const { category } = await fetchCategoryPageData(categoryName);

  if (!category) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      category,
    },
    revalidate: SEVEN_DAYS,
  };
};

/**
 * -------------------------------------------------------------------------
 * Static Path Generation
 * - Get navigatable service skus
 * - Pluck out just the seoName for pathing for routes
 *
 * @returns {Promise<{paths: RouteSkuParams<string>[] | undefined; fallback: boolean}>}
 */
type TRouteSkuParams = {
  params: {
    categoryName: string;
  };
};

export async function getStaticPaths<GetStaticPaths>() {
  const { data } = await skus.all();

  const allSkus = data?.routes?.service_categories.reduce((skusRoute: TRouteSkuParams[], item: { id: number; seo_name: string }) => {
    const allColumns = headerData.servicesMenu.map(column => column.categories).flat();
    const currentPage = allColumns.find(column => column.id === item.id);
    const stringSeoName = String(item.seo_name);

    if (!stringSeoName || stringSeoName === 'undefined' || !currentPage) return skusRoute;

    // Exclude 'home-cleaning' from dynamic route since it has its own static page
    if (stringSeoName === 'home-cleaning') return skusRoute;

    skusRoute.push({
      params: {
        categoryName: stringSeoName,
      },
    });

    return skusRoute;
  }, []);

  return {
    paths: allSkus,
    fallback: true,
  };
}
